import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { ArrowLeft, Download, Mail, Phone, Sparkles, Trash2, Users } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '../../../../Redux/Hooks'
import { removeCurrentFolder } from '../../../../Redux/Slice/Dashboard/AlbumSlice'
import {
  getAllRedeemUserAPI,
  removeRedeemUserAPI,
} from '../../../../Redux/ApiCalls/Dashboard/AlbumAPI'
import { acceptRedeemContactTermsAPI } from '../../../../Redux/ApiCalls/Auth/user'
import { getUserByToken } from '../../../../Redux/ApiCalls/Auth/login'
import { setShowRedeemUserMobile } from '../../../../Redux/Slice/Auth/AuthSlice'
import { RedeemUsers } from '../../../../Data/user.dto'
import { resolveShowRedeemUserMobile } from '../../../../Utils/resolveShowRedeemUserMobile'
import DefaultImagePNG from '../../../../assets/images/DefaultProfilePic.png'
import { useNavigate } from 'react-router-dom'
import DeletePopup from '../Folder/DeletePopup'
import LoadingDots from '../../Utlis/LoadinDots'
import { IMAGE_CDN } from '../../../../config/config'
import MarketplaceEmptyState from '../../../organisms/Dashboard/Marketplace/MarketplaceEmptyState'
import PermissionGate from '../../Utlis/PermissionGate'
import RedeemContactTermsPanel from './RedeemContactTermsPanel'
import {
  PageContainer,
  ScrollBody,
  FormCard,
  CardHeader,
  CardIcon,
  CardTitle,
  CardSubtitle,
  DeleteBtn,
  gradient,
} from '../../../molecules/Dashboard/SingleAlbumPage/albumFormSharedStyles'
import {
  Hero,
  HeroGlow,
  HeroRow,
  HeroLeft,
  BackButton,
  HeroText,
  HeroBadge,
  PageTitle,
  PageSubtitle,
} from '../../../molecules/Dashboard/Member/memberFormStyles'
import * as XLSX from 'xlsx'

const shouldShowRedeemUser = (user: RedeemUsers) => {
  const hasName = Boolean(user.name?.trim())
  const hasEmail = Boolean(user.email?.trim())
  return hasName || hasEmail
}

const RedeemUser: React.FC = () => {
  const { currentAlbum, redeemUsers, loading, isRedeemUserUpdates } =
    useAppSelector((state) => state.album)
  const dispatch = useAppDispatch()
  const [currentRedeemUsers, setCurrentRedeemUsers] = useState<RedeemUsers[]>([])
  const [removeUserPopup, setRemoveUserPopup] = useState(false)
  const [selectedUser, setSelectedUser] = useState<RedeemUsers>()
  /** null = still reading user-details_v2 */
  const [needsTerms, setNeedsTerms] = useState<boolean | null>(null)
  const [consentSubmitting, setConsentSubmitting] = useState(false)
  const navigate = useNavigate()

  const hasRedeemContactConsent = needsTerms === false

  // Gate on fresh /account/user-details_v2/ → show_redeem_user_mobile
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const payload = await dispatch(getUserByToken()).unwrap()
        const allowed = resolveShowRedeemUserMobile(payload)
        if (cancelled) return
        dispatch(setShowRedeemUserMobile(allowed))
        setNeedsTerms(!allowed)
      } catch {
        if (cancelled) return
        // If refresh fails, treat as not agreed (safer — show T&C)
        setNeedsTerms(true)
      }
    })()
    return () => {
      cancelled = true
      dispatch(removeCurrentFolder())
    }
  }, [dispatch])

  useEffect(() => {
    if (hasRedeemContactConsent && currentAlbum) {
      dispatch(getAllRedeemUserAPI({ album_id: currentAlbum.id }))
    }
  }, [hasRedeemContactConsent, currentAlbum, dispatch])

  useEffect(() => {
    if (hasRedeemContactConsent && currentAlbum && isRedeemUserUpdates) {
      dispatch(getAllRedeemUserAPI({ album_id: currentAlbum.id }))
    }
  }, [isRedeemUserUpdates, hasRedeemContactConsent, currentAlbum, dispatch])

  useEffect(() => {
    setCurrentRedeemUsers((redeemUsers ?? []).filter(shouldShowRedeemUser))
  }, [redeemUsers])

  const handleAgreeTerms = async () => {
    setConsentSubmitting(true)
    try {
      await dispatch(acceptRedeemContactTermsAPI()).unwrap()
      dispatch(setShowRedeemUserMobile(true))
      setNeedsTerms(false)
    } catch {
      // toast handled in reducer
    } finally {
      setConsentSubmitting(false)
    }
  }

  const handleDeclineTerms = () => {
    navigate(-1)
  }

  const handleRemoveRedeemUser = (user_id: number, album_id: number) => {
    dispatch(removeRedeemUserAPI({ album_id, user_id }))
    setRemoveUserPopup(false)
  }

  if (needsTerms === null) {
    return (
      <PageContainer>
        <LoadingWrap>
          <LoadingDots />
          <LoadingHint>Checking permission…</LoadingHint>
        </LoadingWrap>
      </PageContainer>
    )
  }

  if (needsTerms) {
    return (
      <PageContainer>
        <Hero>
          <HeroGlow />
          <HeroRow>
            <HeroLeft>
              <BackButton type="button" onClick={handleDeclineTerms} aria-label="Go back">
                <ArrowLeft size={18} />
              </BackButton>
              <HeroText>
                <div className="flex flex-col">
                  <HeroBadge>
                    <Sparkles size={12} />
                    Album access
                  </HeroBadge>
                  <PageTitle className="linear-text">
                    {currentAlbum?.name || 'Redeem users'}
                  </PageTitle>
                  <PageSubtitle>
                    Accept Terms &amp; Conditions once to view redeem code users.
                  </PageSubtitle>
                </div>
              </HeroText>
            </HeroLeft>
          </HeroRow>
        </Hero>

        <ScrollBody>
          <FormCard>
            <RedeemContactTermsPanel
              loading={consentSubmitting}
              onAgree={handleAgreeTerms}
              onDecline={handleDeclineTerms}
            />
          </FormCard>
        </ScrollBody>
      </PageContainer>
    )
  }

  const handleExportExcel = () => {
    if (!currentRedeemUsers.length) return

    const data = currentRedeemUsers.map((user, index) => ({
      'S.No': index + 1,
      Name: user.name || 'Unknown user',
      Email: user.email || 'Not Provided',
      'Phone Code': user.country_code || '',
      'Phone Number': user.phone_number || '',
    }))

    const worksheet = XLSX.utils.json_to_sheet(data)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Redeem Users')
    XLSX.writeFile(workbook, `${currentAlbum?.name || 'album'}-redeem-users.xlsx`)
  }

  if (currentAlbum && loading) {
    return (
      <PageContainer>
        <LoadingWrap>
          <LoadingDots />
          <LoadingHint>Loading redeem code users…</LoadingHint>
        </LoadingWrap>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      {createPortal(
        removeUserPopup && selectedUser && currentAlbum ? (
          <DeletePopup
            buttonText="Remove"
            cancel={() => setRemoveUserPopup(false)}
            Delete={() => handleRemoveRedeemUser(selectedUser.id, currentAlbum.id)}
            text="Are you sure you want to remove this redeem user?"
          />
        ) : null,
        document.body
      )}

      <Hero>
        <HeroGlow />
        <HeroRow>
          <HeroLeft>
            <BackButton type="button" onClick={() => navigate(-1)} aria-label="Go back">
              <ArrowLeft size={18} />
            </BackButton>
            <HeroText>
              <div className="flex flex-col">
                <HeroBadge>
                  <Sparkles size={12} />
                  Album access
                </HeroBadge>
                <PageTitle className="linear-text">
                  {currentAlbum?.name || 'Redeem users'}
                </PageTitle>
                <PageSubtitle>
                  People who redeemed your album code and can view shared photos.
                </PageSubtitle>
              </div>
            </HeroText>
          </HeroLeft>
          <HeroActions>
            <CountPill>
              <Users size={13} />
              {currentRedeemUsers.length} user{currentRedeemUsers.length === 1 ? '' : 's'}
            </CountPill>
            <ExportBtn
              type="button"
              disabled={currentRedeemUsers.length === 0}
              onClick={handleExportExcel}
            >
              <Download size={14} />
              Export Excel
            </ExportBtn>
          </HeroActions>
        </HeroRow>
      </Hero>

      <ScrollBody>
        <FormCard>
          <CardHeader>
            <CardIcon $tone="#c94daa">
              <Users size={18} />
            </CardIcon>
            <div>
              <CardTitle>Redeem code users</CardTitle>
              <CardSubtitle>
                Manage customers who accessed this album with a redeem code.
              </CardSubtitle>
            </div>
          </CardHeader>

          {currentRedeemUsers.length > 0 ? (
            <UserList>
              {currentRedeemUsers.map((user) => (
                <UserCard key={user.id}>
                  <Avatar
                    src={
                      user.profile_image
                        ? `${IMAGE_CDN}/${user.profile_image}`
                        : DefaultImagePNG
                    }
                    alt={user.name ? `${user.name} profile` : 'User profile'}
                  />
                  <UserInfo>
                    <UserName>{user.name || user.email || 'Unknown user'}</UserName>
                    {user.email && (
                      <ContactRow>
                        <Mail size={12} />
                        {user.email}
                      </ContactRow>
                    )}
                    {(user.country_code || user.phone_number) && (
                      <ContactRow>
                        <Phone size={12} />
                        {`${user.country_code ?? ''} ${user.phone_number ?? ''}`.trim()}
                      </ContactRow>
                    )}
                  </UserInfo>
                  <PermissionGate anyPermission={["albums.delete", "albums.redeem_code_users"]}>
                    <RemoveBtn
                      type="button"
                      onClick={() => {
                        setSelectedUser(user)
                        setRemoveUserPopup(true)
                      }}
                    >
                      <Trash2 size={14} />
                      Remove
                    </RemoveBtn>
                  </PermissionGate>
                </UserCard>
              ))}
            </UserList>
          ) : (
            <MarketplaceEmptyState
              icon={<Users size={28} />}
              title="No redeem users yet"
              description="When someone redeems your album code, they will appear here so you can manage their access."
              hint={{
                text: 'Share your album redeem code from the album share screen',
              }}
              padding="8px 0 16px"
            />
          )}
        </FormCard>
      </ScrollBody>
    </PageContainer>
  )
}

export default RedeemUser

const LoadingWrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 280px;
`

const LoadingHint = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.55);
`

const HeroActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  flex-shrink: 0;
`

const CountPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #c94daa;
`

const ExportBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 16px;
  border: none;
  border-radius: 12px;
  background: ${gradient};
  color: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(201, 77, 170, 0.22);
  transition: transform 0.15s ease;
  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }
  &:disabled {
    background: rgba(23, 23, 23, 0.12);
    color: rgba(23, 23, 23, 0.4);
    cursor: not-allowed;
    box-shadow: none;
  }
`

const UserList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const UserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 16px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  background: #fafafa;
  transition: all 0.15s ease;

  &:hover {
    border-color: rgba(201, 77, 170, 0.18);
    background: rgba(201, 77, 170, 0.04);
    transform: translateY(-1px);
  }
`

const Avatar = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
`

const UserInfo = styled.div`
  min-width: 0;
  flex: 1;
`

const UserName = styled.p`
  margin: 0 0 4px;
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #171717;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ContactRow = styled.p`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.52);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  & + & {
    margin-top: 2px;
  }

  svg {
    flex-shrink: 0;
    opacity: 0.7;
  }
`

const RemoveBtn = styled(DeleteBtn)`
  height: 38px;
  padding: 0 14px;
  font-size: 12px;
  flex-shrink: 0;
`
